import { Loader2 } from 'lucide-react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { GetSubmissionAttemptDetailResponse } from '@/services/api/agentflowService';
import {
  useRebuildSubmissionAttemptMutation,
  useSubmitSubmissionAttemptMutation,
} from '@/services/api/agentflowService';

import {
  getSubmissionAttemptPresentationState,
  getSubmissionAttemptRebuildHelperText,
  getSubmissionAttemptSummary,
} from './submissionAttemptPresentation';

interface SubmissionAttemptActionsProps {
  detail: GetSubmissionAttemptDetailResponse;
  matterId: string;
  requestId: string;
  onClose: () => void;
  onOpenRequestModal: () => void;
}

export const SubmissionAttemptActions = ({
  detail,
  matterId,
  requestId,
  onClose,
  onOpenRequestModal,
}: SubmissionAttemptActionsProps) => {
  const [submitAttempt, { isLoading: isSubmitting }] =
    useSubmitSubmissionAttemptMutation();
  const [rebuild, { isLoading: isRebuilding }] =
    useRebuildSubmissionAttemptMutation();

  const handleOpenRequestModal = useCallback(() => {
    onClose();
    onOpenRequestModal();
  }, [onClose, onOpenRequestModal]);

  const handleSubmitPackage = useCallback(async () => {
    try {
      await submitAttempt({ matterId, requestId }).unwrap();
      toast.success('Package submission started');
      onClose();
    } catch {
      toast.error('Failed to submit package');
    }
  }, [matterId, onClose, requestId, submitAttempt]);

  const handleRebuild = useCallback(async () => {
    try {
      await rebuild({ matterId, requestId }).unwrap();
      toast.success('Package rebuilt');
    } catch {
      toast.error('Failed to rebuild package');
    }
  }, [matterId, requestId, rebuild]);

  const isReady = detail.status === 'ready';
  const isSubmittingState = detail.status === 'submitting';
  const isBlocked =
    detail.status === 'blocked' ||
    detail.status === 'needs_rebuild' ||
    detail.status === 'needs_re_evaluation';
  const presentationState = getSubmissionAttemptPresentationState(detail);
  const summary = getSubmissionAttemptSummary(detail);
  const canRebuild = isBlocked && presentationState.manualInputCount === 0;
  const helperText = isBlocked ? getSubmissionAttemptRebuildHelperText(detail) : '';
  const showHelperText = isBlocked && Boolean(helperText);

  return (
    <div className="border-t border-zinc-200 px-5 py-3 space-y-2 shrink-0">
      {(isReady || isSubmittingState) && (
        <>
          <Button
            className={cn(
              'w-full gap-2',
              isSubmittingState &&
                'bg-blue-600 text-white hover:bg-blue-600 disabled:opacity-100',
            )}
            onClick={isReady ? handleSubmitPackage : undefined}
            disabled={isSubmitting || isSubmittingState}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSubmittingState ? 'Submitting Package' : 'Submit Package'}
          </Button>
          <p className="text-[10px] text-zinc-500 text-center">
            Promotes this draft into the HIPAA package and starts submission
            with the agent&apos;s selections.
          </p>
        </>
      )}

      {detail.status === 'submitted' && (
        <>
          <Button className="w-full" disabled>
            Package Submitted
          </Button>
          <p className="text-[10px] text-zinc-500 text-center">
            This package has already been submitted.
          </p>
        </>
      )}

      {isBlocked && (
        <>
          <Button
            className="w-full gap-2"
            variant={canRebuild ? 'default' : 'outline'}
            onClick={handleRebuild}
            disabled={!canRebuild || isRebuilding}
          >
            {isRebuilding && <Loader2 className="h-4 w-4 animate-spin" />}
            Rebuild Package
          </Button>
          {showHelperText && (
            <p className="text-[10px] text-zinc-500 text-center leading-relaxed">
              {helperText}
            </p>
          )}
          {presentationState.reviewBlocked && summary.description && (
            <p className="text-[10px] text-zinc-500 text-center leading-relaxed">
              Non-blocking blank fields shown above were left blank intentionally.
            </p>
          )}
        </>
      )}

      <button
        type="button"
        className="w-full text-center text-xs font-medium text-zinc-500 hover:text-zinc-700 py-1"
        onClick={handleOpenRequestModal}
      >
        Configure &amp; submit manually instead
      </button>
    </div>
  );
};
